<div class="theme-container settings-page">

  <div class="settings-page__content">
    @if (editingTheme(); as theme) {
      <section class="theme-editor">
        <div class="theme-editor__layout">
          <div class="theme-editor__form">
            <div class="theme-editor__content-header">
              <h2>{{ editorTitleKey() | translate }}</h2>
              <div class="theme-editor__actions">
                @if (editorMode() === 'edit') {
                  @if (theme.active) {
                    <span class="active-pill">
                      <span class="active-pill__dot"></span>
                      {{ 'SETTINGS_THEME.STATUS.ACTIVE' | translate }}
                    </span>
                  } @else {
                    <p-button
                      [label]="'SETTINGS_THEME.ACTIONS.SET_ACTIVE' | translate"
                      severity="secondary"
                      [outlined]="true"
                      [disabled]="!canUpdateTheme() || mutatingThemeKey() === previewKey(theme)"
                      [loading]="mutatingThemeKey() === previewKey(theme)"
                      (onClick)="activateTheme(theme)" />
                  }
                }
                <p-button
                  [label]="'SETTINGS_THEME.ACTIONS.REVERT' | translate"
                  severity="secondary"
                  [text]="true"
                  [disabled]="!hasEditorChanges() || isSaving()"
                  (onClick)="discardChanges()" />
              </div>
            </div>

            <section class="theme-section">
              <div class="theme-fields theme-fields--one">
                <div class="theme-field theme-field--full">
                  <label for="theme-name">
                    {{ 'SETTINGS_THEME.FIELDS.THEME_NAME' | translate }}
                  </label>
                  <small>{{ 'SETTINGS_THEME.FIELDS.THEME_NAME_DESC' | translate }}</small>
                  <input
                    pInputText
                    id="theme-name"
                    [ngModel]="editorName()"
                    (ngModelChange)="onNameChange($event)"
                    class="w-full" />
                </div>
                <div class="theme-field theme-field--full">
                  <label for="theme-description">
                    {{ 'SETTINGS_THEME.FIELDS.THEME_DESCRIPTION' | translate }}
                  </label>
                  <small>{{ 'SETTINGS_THEME.FIELDS.THEME_DESCRIPTION_DESC' | translate }}</small>
                  <input
                    pInputText
                    id="theme-description"
                    [ngModel]="editorDescription()"
                    (ngModelChange)="onDescriptionChange($event)"
                    class="w-full" />
                </div>
              </div>
            </section>

            @if (editorError(); as error) {
              <p class="theme-inline-error" role="alert">{{ error }}</p>
            }

            <div class="theme-mode-tabs">
              <div
                class="theme-mode-tabs__buttons"
                role="tablist"
                [attr.aria-label]="'SETTINGS_THEME.MODE.LABEL' | translate">
                <button
                  type="button"
                  role="tab"
                  class="theme-mode-tab"
                  [class.active]="activeMode() === 'light'"
                  [attr.aria-selected]="activeMode() === 'light'"
                  (click)="onModeChange('light')">
                  <i class="pi pi-sun" aria-hidden="true"></i>
                  {{ 'SETTINGS_THEME.MODE.LIGHT' | translate }}
                </button>
                <button
                  type="button"
                  role="tab"
                  class="theme-mode-tab"
                  [class.active]="activeMode() === 'dark'"
                  [attr.aria-selected]="activeMode() === 'dark'"
                  (click)="onModeChange('dark')">
                  <i class="pi pi-moon" aria-hidden="true"></i>
                  {{ 'SETTINGS_THEME.MODE.DARK' | translate }}
                </button>
              </div>
              <span class="theme-mode-tabs__hint">
                {{ 'SETTINGS_THEME.MODE.EDITING_HINT' | translate: { mode: activeMode() } }}
              </span>
            </div>

            <section class="theme-section">
              <header>
                <h3>{{ 'SETTINGS_THEME.SECTIONS.COLORS' | translate }}</h3>
                <p>{{ 'SETTINGS_THEME.SECTIONS.COLORS_DESC' | translate }}</p>
              </header>
              <div class="theme-fields">
                @for (field of colorFields; track field.key) {
                  <ng-container
                    [ngTemplateOutlet]="colorField"
                    [ngTemplateOutletContext]="{ field: field }" />
                }
              </div>
            </section>

            <section class="theme-section">
              <header>
                <h3>{{ 'SETTINGS_THEME.SECTIONS.SURFACES' | translate }}</h3>
                <p>{{ 'SETTINGS_THEME.SECTIONS.SURFACES_DESC' | translate }}</p>
              </header>
              <div class="theme-fields">
                @for (field of surfaceColorFields; track field.nestedKey) {
                  <ng-container
                    [ngTemplateOutlet]="colorField"
                    [ngTemplateOutletContext]="{ field: field }" />
                }
              </div>
            </section>

            <section class="theme-section">
              <header>
                <h3>{{ 'SETTINGS_THEME.SECTIONS.HEADER' | translate }}</h3>
                <p>{{ 'SETTINGS_THEME.SECTIONS.HEADER_DESC' | translate }}</p>
              </header>
              <div class="theme-fields">
                @for (field of headerColorFields; track field.nestedKey) {
                  <ng-container
                    [ngTemplateOutlet]="colorField"
                    [ngTemplateOutletContext]="{ field: field }" />
                }
              </div>
            </section>

            <section class="theme-section">
              <header>
                <h3>{{ 'SETTINGS_THEME.SECTIONS.SIDEBAR' | translate }}</h3>
                <p>{{ 'SETTINGS_THEME.SECTIONS.SIDEBAR_DESC' | translate }}</p>
              </header>
              <div class="theme-fields">
                @for (field of sidebarColorFields; track field.nestedKey) {
                  <ng-container
                    [ngTemplateOutlet]="colorField"
                    [ngTemplateOutletContext]="{ field: field }" />
                }
              </div>
            </section>

            <section class="theme-section">
              <header>
                <h3>{{ 'SETTINGS_THEME.SECTIONS.TYPOGRAPHY' | translate }}</h3>
                <p>{{ 'SETTINGS_THEME.SECTIONS.TYPOGRAPHY_DESC' | translate }}</p>
              </header>
              <div class="theme-fields">
                <div class="theme-field">
                  <label for="theme-font-style">
                    {{ 'SETTINGS_THEME.FIELDS.FONT_STYLE' | translate }}
                  </label>
                  <small>{{ 'SETTINGS_THEME.FIELDS.FONT_STYLE_DESC' | translate }}</small>
                  <p-select
                    id="theme-font-style"
                    [options]="fontStyleOptions"
                    [ngModel]="currentMode()?.baseFontStyle"
                    (ngModelChange)="onFontStyleChange($event)"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full" />
                </div>

                <div class="theme-field">
                  <label for="theme-font-size">
                    {{ 'SETTINGS_THEME.FIELDS.FONT_SIZE' | translate }}
                  </label>
                  <small>{{ 'SETTINGS_THEME.FIELDS.FONT_SIZE_DESC' | translate }}</small>
                  <p-inputGroup>
                    <input
                      pInputText
                      id="theme-font-size"
                      type="number"
                      [ngModel]="getFontSizeNumeric()"
                      (ngModelChange)="onFontSizeChange($event)"
                      [class.ng-invalid]="getValidationError('baseFontSize')"
                      [attr.aria-invalid]="getValidationError('baseFontSize') ? true : null"
                      [attr.aria-describedby]="
                        getValidationError('baseFontSize') ? 'theme-font-size-error' : null
                      " />
                    <p-inputGroupAddon>%</p-inputGroupAddon>
                  </p-inputGroup>
                  @if (getValidationError('baseFontSize'); as error) {
                    <small class="p-error" id="theme-font-size-error" role="alert">
                      {{ error }}
                    </small>
                  }
                </div>
              </div>
            </section>
          </div>

          <aside class="theme-editor__preview">
            <div class="theme-editor__preview-sticky">
              <div class="theme-editor__preview-head">
                <h3>{{ 'SETTINGS_THEME.SECTIONS.PREVIEW' | translate }}</h3>
                <span>
                  {{ 'SETTINGS_THEME.PREVIEW.MODE_HINT' | translate: { mode: activeMode() } }}
                </span>
              </div>
              @if (editorPreviewTheme(); as previewTheme) {
                <div class="theme-editor__preview-frame theme-editor__preview-grid">
                  <tf-settings-theme-preview
                    [theme]="previewTheme"
                    [mode]="activeMode()"
                    size="large"
                    [interactive]="true" />
                  <tf-settings-theme-preview
                    [theme]="previewTheme"
                    [mode]="activeMode()"
                    size="large"
                    variant="login"
                    [interactive]="true" />
                </div>
              }
            </div>
          </aside>
        </div>
      </section>
    } @else {
      <section class="themes-list">
        <div class="themes-list__header">
          <p class="themes-list__description">{{ 'SETTINGS_THEME.DESCRIPTION' | translate }}</p>
          @if (showListActions()) {
            <div class="themes-list__actions">
              <p-button
                icon="pi pi-refresh"
                severity="secondary"
                [outlined]="true"
                [loading]="isLoading()"
                [disabled]="isLoading()"
                [attr.aria-label]="'GENERAL.REFRESH' | translate"
                [pTooltip]="'GENERAL.REFRESH' | translate"
                tooltipPosition="top"
                (onClick)="reload()" />
              <p-button
                icon="pi pi-plus"
                [label]="'SETTINGS_THEME.ACTIONS.NEW_THEME' | translate"
                [disabled]="!canCreateTheme()"
                [loading]="isSaving()"
                (onClick)="createTheme()" />
            </div>
          }
        </div>

        @if (listError(); as error) {
          @if (initialLoadFailed()) {
            <tf-settings-load-error
              [title]="error"
              [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
              [details]="listLoadErrorDetails()"
              [retrying]="isLoading()"
              (retry)="reload()" />
          } @else {
            <tf-settings-inline-message
              [message]="error"
              messageId="theme-list-load-error"
              (dismissed)="clearListError()" />
          }
        }

        @if (!initialLoadFailed() && !canManageTheme()) {
          <div class="permission-banner">
            <span class="permission-banner__icon">
              <i class="pi pi-lock" aria-hidden="true"></i>
            </span>
            <div>
              <h3>{{ 'SETTINGS_THEME.NO_PERMISSION.TITLE' | translate }}</h3>
              <p>{{ 'SETTINGS_THEME.NO_PERMISSION.BODY' | translate }}</p>
            </div>
          </div>
        }

        @if (!initialLoadFailed()) {
          @if (!hasLoadedOnce()) {
            <div class="themes-grid" aria-busy="true">
              @for (_ of skeletonCount; track $index) {
                <tf-settings-card class="theme-card-skeleton">
                  <div settingsCardPreview class="theme-card__preview-panel">
                    <div class="skeleton__preview"></div>
                  </div>
                  <div settingsCardContent class="theme-card-skeleton__body">
                    <span class="skeleton__bar w-50"></span>
                    <span class="skeleton__bar w-80"></span>
                  </div>
                  <tf-settings-card-actions>
                    <span settingsCardActionsStart class="skeleton__bar button"></span>
                    <span settingsCardActionsEnd class="skeleton__bar action"></span>
                  </tf-settings-card-actions>
                </tf-settings-card>
              }
            </div>
          } @else {
            <div class="themes-grid">
              @for (theme of themes(); track previewKey(theme)) {
                <tf-settings-card
                  [title]="theme.name"
                  [subtitle]="theme.description"
                  [active]="theme.active"
                  [disabled]="!canManageTheme()">
                  <div settingsCardPreview class="theme-card__preview-panel">
                    <tf-settings-theme-preview
                      [theme]="theme.theme"
                      [mode]="previewMode(theme)"
                      size="card" />
                  </div>
                  @if (theme.linkedBrandCount > 0) {
                    <div settingsCardContent class="theme-card__linked-wrap">
                      <button
                        type="button"
                        class="theme-card__linked"
                        [attr.aria-label]="linkedBrandsLabel(theme)"
                        aria-haspopup="dialog"
                        (click)="toggleLinkedBrandsPopover($event, linkedBrandsPopover)">
                        {{ linkedBrandsLabel(theme) }}
                      </button>
                      <p-popover
                        #linkedBrandsPopover
                        appendTo="body"
                        styleClass="theme-linked-brands-popover"
                        [focusOnShow]="false">
                        <tf-settings-theme-linked-brands-popover
                          [brands]="linkedBrandsForTheme(theme)" />
                      </p-popover>
                    </div>
                  }

                  <button
                    settingsCardAccessory
                    type="button"
                    class="mode-toggle mode-toggle--vertical"
                    [attr.aria-label]="'SETTINGS_THEME.PREVIEW.MODE_TOGGLE' | translate"
                    [attr.aria-pressed]="previewMode(theme) === 'dark'"
                    (click)="togglePreviewMode(theme)">
                    <span
                      class="mode-toggle__option"
                      [class.active]="previewMode(theme) === 'light'">
                      <i class="pi pi-sun" aria-hidden="true"></i>
                    </span>
                    <span
                      class="mode-toggle__option"
                      [class.active]="previewMode(theme) === 'dark'">
                      <i class="pi pi-moon" aria-hidden="true"></i>
                    </span>
                  </button>

                  <tf-settings-card-actions>
                    <span settingsCardActionsStart>
                      @if (theme.active) {
                        <span class="active-pill">
                          <span class="active-pill__dot"></span>
                          {{ 'SETTINGS_THEME.STATUS.ACTIVE' | translate }}
                        </span>
                      } @else {
                        <p-button
                          size="small"
                          severity="secondary"
                          [outlined]="true"
                          [label]="'SETTINGS_THEME.ACTIONS.SET_ACTIVE' | translate"
                          [disabled]="!canUpdateTheme() || mutatingThemeKey() === previewKey(theme)"
                          [loading]="mutatingThemeKey() === previewKey(theme)"
                          (onClick)="activateTheme(theme)" />
                      }
                    </span>

                    <span settingsCardActionsEnd class="theme-card__actions">
                      <p-button
                        icon="pi pi-copy"
                        severity="secondary"
                        [text]="true"
                        [rounded]="true"
                        size="small"
                        [disabled]="!canCreateTheme() || mutatingThemeKey() === previewKey(theme)"
                        [attr.aria-label]="'SETTINGS_THEME.ACTIONS.DUPLICATE' | translate"
                        [pTooltip]="'SETTINGS_THEME.ACTIONS.DUPLICATE' | translate"
                        tooltipPosition="top"
                        (onClick)="duplicateTheme(theme)" />
                      @if (!theme.builtIn) {
                        <p-button
                          icon="pi pi-pencil"
                          severity="secondary"
                          [text]="true"
                          [rounded]="true"
                          size="small"
                          [disabled]="!canUpdateTheme()"
                          [attr.aria-label]="'SETTINGS_THEME.ACTIONS.EDIT' | translate"
                          [pTooltip]="'SETTINGS_THEME.ACTIONS.EDIT' | translate"
                          tooltipPosition="top"
                          (onClick)="openEditor(theme)" />
                      }
                      @if (!theme.builtIn && !theme.active) {
                        <p-button
                          icon="pi pi-trash"
                          severity="danger"
                          [text]="true"
                          [rounded]="true"
                          size="small"
                          [disabled]="!canDeleteTheme() || mutatingThemeKey() === previewKey(theme)"
                          [attr.aria-label]="'SETTINGS_THEME.ACTIONS.DELETE' | translate"
                          [pTooltip]="'SETTINGS_THEME.ACTIONS.DELETE' | translate"
                          tooltipPosition="top"
                          (onClick)="confirmDeleteTheme(theme)" />
                      }
                    </span>
                  </tf-settings-card-actions>
                  @if (actionError(theme); as error) {
                    <div settingsCardError role="alert">{{ error }}</div>
                  }
                </tf-settings-card>
              }

              @if (canCreateTheme()) {
                <tf-settings-card-new
                  [title]="'SETTINGS_THEME.ACTIONS.NEW_THEME' | translate"
                  [hint]="'SETTINGS_THEME.ACTIONS.NEW_THEME_HINT' | translate"
                  [disabled]="isSaving()"
                  (cardClick)="createTheme()" />
              }
            </div>
          }
        }
      </section>
    }
  </div>
  @if (editingTheme()) {
    <tf-settings-actions
      [cancelDisabled]="isSaving()"
      [saveDisabled]="isSaveDisabled()"
      [loading]="isSaving()"
      [saveLabelKey]="'SETTINGS_THEME.ACTIONS.SAVE_THEME'"
      (cancelled)="closeEditor()"
      (save)="saveTheme()" />
  }
</div>

<ng-template #colorField let-field="field">
  <div class="theme-field">
    <label [for]="getFieldId(field.key, field.nestedKey)">
      {{ field.labelKey | translate }}
    </label>
    <small>{{ field.descriptionKey | translate }}</small>
    <p-inputGroup>
      <input
        pInputText
        [id]="getFieldId(field.key, field.nestedKey)"
        [ngModel]="getFieldValue(field.key, field.nestedKey)"
        (ngModelChange)="onColorChange(field.key, field.nestedKey, $event)"
        placeholder="#000000"
        maxlength="7"
        class="theme-color-hex-input"
        [class.ng-invalid]="getValidationError(field.key, field.nestedKey)"
        [attr.aria-invalid]="getValidationError(field.key, field.nestedKey) ? true : null"
        [attr.aria-describedby]="
          getValidationError(field.key, field.nestedKey)
            ? getFieldId(field.key, field.nestedKey) + '-error'
            : null
        " />
      <p-inputGroupAddon class="theme-color-picker-addon">
        <p-colorPicker
          [ngModel]="getColorPickerValue(field.key, field.nestedKey)"
          (ngModelChange)="onColorPickerChange(field.key, field.nestedKey, $event)"
          appendTo="body" />
      </p-inputGroupAddon>
    </p-inputGroup>
    @if (getValidationError(field.key, field.nestedKey); as error) {
      <small class="p-error" [id]="getFieldId(field.key, field.nestedKey) + '-error'" role="alert">
        {{ error }}
      </small>
    }
  </div>
</ng-template>

<p-confirmDialog />
